import { applyAction } from '$app/forms';
import { env } from '$env/dynamic/public';
import ActionLink from '$lib/components/molecules/Toast/customComponents/ActionLink/ActionLink.svelte';
import { addToast } from '$lib/components/molecules/Toast/Toast.svelte';
import { MAP_LANGUAGE_TO_SALESFORCE_LANGUAGE_PARAM } from '$lib/consts';

/**
 * Gets Salesforce login page URL with the necessary query parameters.
 *
 * @param {language} [lang] - language code - it/fr/end/de which is then mapped into SalesForce language param and passed into URL to show correct page translation.
 * @param {string} [feUrl=window.location.href] - The front-end URL to be included in the state parameter for redirection after login.
 *
 * The function constructs the Salesforce login URL using environment variables for the following parameters:
 * - `PUBLIC_SALESFORCE_LOGIN_URL`: The base URL for Salesforce login.
 * - `PUBLIC_SALESFORCE_RESPONSE_TYPE`: The response type for the OAuth2 flow.
 * - `PUBLIC_SALESFORCE_CLIENT_ID`: The client ID for the Salesforce application.
 * - `PUBLIC_SALESFORCE_BE_REDIRECT_URL`: The backend redirect URL after login on salesforce.
 * - `PUBLIC_SALESFORCE_SCOPE`: The scope of the OAuth2 request.
 * ----
 * - `language` - which is generated based on provided language and then properly formatted depending on locales language/country code.
 *
 */
export const getSFLoginUrl = (
	language: string,
	feUrl = `${window.location.pathname}${window.location.search}`
) => {
	const lang = MAP_LANGUAGE_TO_SALESFORCE_LANGUAGE_PARAM[language];

	const url = new URL(env.PUBLIC_SALESFORCE_LOGIN_URL ?? 'test');
	url.searchParams.append('response_type', env.PUBLIC_SALESFORCE_RESPONSE_TYPE ?? '');
	url.searchParams.append('client_id', env.PUBLIC_SALESFORCE_CLIENT_ID ?? '');
	url.searchParams.append('redirect_uri', env.PUBLIC_SALESFORCE_BE_REDIRECT_URL ?? '');
	url.searchParams.append('state', feUrl);
	url.searchParams.append('scope', env.PUBLIC_SALESFORCE_SCOPE ?? '');
	url.searchParams.append('language', lang);

	// Add any additional query parameters from the environment variable
	if (env.PUBLIC_SALESFORCE_LOGIN_PARAMS) {
		const params = env.PUBLIC_SALESFORCE_LOGIN_PARAMS.split(';');
		params.forEach((param) => {
			const [key, value] = param.split('=');
			if (key && value) {
				url.searchParams.append(key, value);
			}
		});
	}

	return url.toString();
};

/**
 * Redirects the user to the Salesforce login page with the necessary query parameters.
 *
 * @param {language} [lang] - language code - it/fr/end/de which is then mapped into SalesForce language param and passed into URL to show correct page translation.
 * @param {string} [feUrl=window.location.href] - The front-end URL to be included in the state parameter for redirection after login.
 *
 */
export const redirectToSalesForceLoginPage = (
	language: string,
	feUrl = `${window.location.pathname}${window.location.search}`
) => {
	const url = getSFLoginUrl(language, feUrl);

	window.location.href = url.toString();
};

export const getLogoutUrl = () => new URL(`${env.PUBLIC_API_SERVER}/v2/investor_portal/logout`);

export const showLogoutToast = (isSuccess: boolean, lang: string) => {
	if (isSuccess) {
		showLogoutSuccessToast(lang);
	} else {
		showLogoutErrorToast();
	}
};

const showLogoutErrorToast = () => {
	addToast({
		data: {
			title: '',
			customComponent: ActionLink,
			customComponentProps: {
				status: 'error',
				key: 'logout',
				clickHandler: async () => {
					const formData = new FormData();
					const response = await fetch('/?/logout', {
						method: 'POST',
						body: formData
					});

					const result = await response.json();
					await applyAction(result);
				}
			},
			type: 'error'
		}
	});
};

const showLogoutSuccessToast = (lang: string) => {
	addToast({
		data: {
			title: '',
			customComponent: ActionLink,
			customComponentProps: {
				status: 'success',
				key: 'logout',
				clickHandler: () => {
					redirectToSalesForceLoginPage(lang);
				}
			},
			type: 'success'
		}
	});
};
